import React, { useState, useEffect } from 'react';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import Button from 'Components/Buttons/Button';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import rawChart from 'assets/unifiedChart/unifiedChart.json';

export interface AccountEntry {
  Code: string;
  GL_ACCOUNT: string;
  Statement: string;
  FS_Caption: string;
  Sub1: string;
  Sub2: string;
  Sub3: string;
  QBO_Type: string;
  QBO_Detailed_Type: string;
  Description: string;
  Require_Reconciliation: string;
  Other_Dimension: string;
}

interface RawAccountEntry {
  Code: string;
  GL_ACCOUNT: string;
  Statement: string;
  FS_Caption: string;
  Sub_account_level1: string;
  Sub_account_level2: string;
  Sub_account_level3: string;
  QBO_Type: string;
  QBO_Detailed_Type: string;
  Description?: string;
  'Require_Reconciliation?': string;
  Other_Dimension?: string;
}

const CHART: AccountEntry[] = (rawChart as RawAccountEntry[]).map((e) => ({
  Code: e.Code,
  GL_ACCOUNT: e.GL_ACCOUNT,
  Statement: e.Statement,
  FS_Caption: e.FS_Caption,
  Sub1: e.Sub_account_level1,
  Sub2: e.Sub_account_level2,
  Sub3: e.Sub_account_level3,
  QBO_Type: e.QBO_Type,
  QBO_Detailed_Type: e.QBO_Detailed_Type,
  Description: e.Description || '',
  Require_Reconciliation: e['Require_Reconciliation?'] || '',
  Other_Dimension: e.Other_Dimension || '',
}));

interface Props {
  onClose: () => void;
}

interface ParsedRow {
  cells: string[];
  code: string;
  category: string;
}

const DEFAULT_CATEGORIES = ['Income', 'Expense', 'Other'];

// possible header aliases for fuzzy matching
const HEADER_ALIASES: Record<keyof AccountEntry, string[]> = {
  Code: ['code'],
  GL_ACCOUNT: [
    'gl account',
    'account',
    'account name',
    'glaccount',
    'name',
    'full name',
  ],
  Statement: ['statement'],
  FS_Caption: ['fs caption', 'caption'],
  Sub1: ['sub account level1', 'sub1', 'sub account 1', 'level1'],
  Sub2: ['sub account level2', 'sub2', 'sub account 2', 'level2'],
  Sub3: ['sub account level3', 'sub3', 'sub account 3', 'level3'],
  QBO_Type: ['qbo type', 'account type', 'type'],
  QBO_Detailed_Type: [
    'qbo detailed type',
    'detail type',
    'detailed type',
    'detail',
  ],
  Description: ['description', 'desc'],
  Require_Reconciliation: [
    'require reconciliation',
    'reconciliation',
    'reconcile',
  ],
  Other_Dimension: ['other dimension', 'dimension'],
};

const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

// helper to split a string into tokens for fuzzy matching
const getTokens = (s: string): string[] =>
  clean(s).split(/\s+/).filter(Boolean);

// precompute tokens for each chart entry to speed up matching
const CHART_TOKENS = CHART.map((e) => ({
  code: e.Code,
  tokens: getTokens(
    `${e.GL_ACCOUNT} ${e.FS_Caption} ${e.Sub1} ${e.Sub2} ${e.Sub3} ${e.QBO_Type} ${e.QBO_Detailed_Type} ${e.Description}`,
  ),
}));

const mapHeaderToField = (header: string): keyof AccountEntry | undefined => {
  const cleaned = clean(header);
  let bestField: keyof AccountEntry | undefined;
  let bestScore = 0;
  Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
    aliases.forEach((a) => {
      const dist = levenshtein(cleaned, clean(a));
      const score = 1 - dist / Math.max(cleaned.length, clean(a).length);
      if (score > bestScore) {
        bestScore = score;
        bestField = field as keyof AccountEntry;
      }
    });
  });
  return bestScore > 0.5 ? bestField : undefined;
};

const levenshtein = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    Array(n + 1).fill(0),
  );
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) dp[i][j] = dp[i - 1][j - 1];
      else
        dp[i][j] = Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]) + 1;
    }
  }
  return dp[m][n];
};

const BatchCategorizationModal: React.FC<Props> = ({ onClose }) => {
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [filterColumn, setFilterColumn] = useState(0);
  const [filterValue, setFilterValue] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [categories, setCategories] = useState<string[]>([
    ...DEFAULT_CATEGORIES,
  ]);
  const [newCategory, setNewCategory] = useState('');
  const [bulkCategory, setBulkCategory] = useState(DEFAULT_CATEGORIES[0]);
  const [excelSheets, setExcelSheets] = useState<string[][][]>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [headerRow, setHeaderRow] = useState(0);

  useEffect(() => {
    if (!categories.includes(bulkCategory)) {
      setBulkCategory(categories[0] || '');
    }
  }, [categories]);

  useEffect(() => {
    if (excelSheets.length > 0) {
      applySheetData(selectedSheet, headerRow);
    }
  }, [excelSheets, selectedSheet, headerRow]);

  const parseCsvText = (text: string) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return;
    const head = lines[0].split(',');
    const dataRows = lines.slice(1).map((l) => l.split(','));
    const sanitizedHead = head.filter(
      (h) => !/^code$/i.test(h) && !/^category$/i.test(h),
    );
    const sanitizedRows = dataRows.map((cells) =>
      cells.filter(
        (_, idx) =>
          !/^code$/i.test(head[idx]) && !/^category$/i.test(head[idx]),
      ),
    );
    setHeaders(['Code', ...sanitizedHead, 'Category']);
    setRows(
      sanitizedRows.map((cells) => ({
        cells,
        code: '',
        category: categories[0] || '',
      })),
    );
    setBulkCategory(categories[0] || '');
    setFilterColumn(0);
    setFilterValue('');
    setSelected(new Set());
  };

  const lettersToIndex = (letters: string) => {
    let num = 0;
    for (let i = 0; i < letters.length; i++) {
      num = num * 26 + (letters.charCodeAt(i) - 64);
    }
    return num - 1;
  };

  const parseSheetXml = (xml: string, shared: string[]): string[][] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');
    const rows: string[][] = [];
    const rowEls = Array.from(doc.getElementsByTagName('row'));
    rowEls.forEach((rowEl) => {
      const cells: string[] = [];
      const cellEls = Array.from(rowEl.getElementsByTagName('c'));
      cellEls.forEach((c) => {
        const ref = c.getAttribute('r') || '';
        const colLetters = ref.match(/[A-Z]+/i)?.[0] || '';
        const colIdx = lettersToIndex(colLetters);
        while (cells.length < colIdx) cells.push('');
        const type = c.getAttribute('t');
        const v = c.getElementsByTagName('v')[0]?.textContent || '';
        const val = type === 's' ? shared[parseInt(v, 10)] || '' : v;
        cells[colIdx] = val;
      });
      rows.push(cells);
    });
    return rows;
  };

  const parseExcelFile = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(buffer);
    const workbookXml = await zip.file('xl/workbook.xml')?.async('string');
    if (!workbookXml) return;
    const parser = new DOMParser();
    const workbookDoc = parser.parseFromString(workbookXml, 'application/xml');
    const sheetEls = Array.from(workbookDoc.getElementsByTagName('sheet'));
    const names = sheetEls.map((s) => s.getAttribute('name') || 'Sheet');
    const sharedXml = await zip.file('xl/sharedStrings.xml')?.async('string');
    let shared: string[] = [];
    if (sharedXml) {
      const sharedDoc = parser.parseFromString(sharedXml, 'application/xml');
      shared = Array.from(sharedDoc.getElementsByTagName('t')).map(
        (t) => t.textContent || '',
      );
    }
    const sheets: string[][][] = [];
    for (let i = 0; i < sheetEls.length; i++) {
      const xml = await zip
        .file(`xl/worksheets/sheet${i + 1}.xml`)
        ?.async('string');
      sheets.push(xml ? parseSheetXml(xml, shared) : []);
    }
    setExcelSheets(sheets);
    setSheetNames(names);
    setSelectedSheet(0);
    setHeaderRow(0);
  };

  const applySheetData = (sheetIdx: number, headerIdx: number) => {
    const sheet = excelSheets[sheetIdx] || [];
    if (sheet.length <= headerIdx) {
      setHeaders([]);
      setRows([]);
      return;
    }
    const headRow = sheet[headerIdx];
    const head = Array.isArray(headRow) ? headRow : [];
    const sanitizedHead = head.filter(
      (h) => !/^code$/i.test(h) && !/^category$/i.test(h),
    );
    const dataRows = sheet
      .slice(headerIdx + 1)
      .map((r) =>
        r.filter(
          (_, idx) =>
            !/^code$/i.test(head[idx]) && !/^category$/i.test(head[idx]),
        ),
      );
    setHeaders(['Code', ...sanitizedHead, 'Category']);
    setRows(
      dataRows.map((cells) => ({
        cells,
        code: '',
        category: categories[0] || '',
      })),
    );
    setBulkCategory(categories[0] || '');
    setFilterColumn(0);
    setFilterValue('');
    setSelected(new Set());
  };

  const addCategory = () => {
    const trimmed = newCategory.trim();
    if (!trimmed || categories.includes(trimmed)) return;
    setCategories((prev) => [...prev, trimmed]);
    setNewCategory('');
    if (!bulkCategory) setBulkCategory(trimmed);
  };

  const removeCategory = (cat: string) => {
    setCategories((prev) => {
      const updated = prev.filter((c) => c !== cat);
      const fallback = updated[0] || '';
      setRows((rowsPrev) =>
        rowsPrev.map((r) =>
          r.category === cat ? { ...r, category: fallback } : r,
        ),
      );
      if (bulkCategory === cat) setBulkCategory(fallback);
      return updated;
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase();
    if (ext.endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = () => {
        parseCsvText(reader.result as string);
      };
      reader.readAsText(file);
    } else if (ext.endsWith('.xlsx')) {
      parseExcelFile(file);
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        parseCsvText(reader.result as string);
      };
      reader.readAsText(file);
    }
  };

  const updateCategory = (index: number, cat: string) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, category: cat } : r)),
    );
  };

  const toggleSelect = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(rows.map((_, i) => i)));
  };

  const clearSelection = () => setSelected(new Set());

  const applyBulkCategory = () => {
    setRows((prev) =>
      prev.map((r, i) =>
        selected.has(i) ? { ...r, category: bulkCategory } : r,
      ),
    );
    setSelected(new Set());
  };

  const autoCode = () => {
    const fieldMap = headers.slice(1, headers.length - 1).map(mapHeaderToField);
    setRows((prev) =>
      prev.map((r) => {
        const rowTokens = getTokens(r.cells.join(' '));
        let best: { score: number; code: string } = { score: -1, code: '' };
        for (let i = 0; i < CHART.length; i++) {
          const entry = CHART[i];
          const entryTokens = CHART_TOKENS[i].tokens;
          let score = 0;
          fieldMap.forEach((field, idx) => {
            if (!field) return;
            const cellVal = r.cells[idx]?.toLowerCase().trim();
            if (!cellVal) return;
            const entryVal = (entry[field] as string)?.toLowerCase().trim();
            if (!entryVal) return;
            if (cellVal === entryVal) score += 5;
            else if (cellVal.includes(entryVal) || entryVal.includes(cellVal))
              score += 3;
            else {
              const dist = levenshtein(cellVal, entryVal);
              const ratio =
                1 - dist / Math.max(cellVal.length, entryVal.length);
              if (ratio > 0.5) score += ratio; // partial similarity
            }
          });

          // token overlap as fallback when headers don't match well
          const overlap = rowTokens.filter((t) =>
            entryTokens.includes(t),
          ).length;
          score += overlap;

          if (score > best.score) best = { score, code: entry.Code };
        }
        return { ...r, code: best.score > 0 ? best.code : r.code };
      }),
    );
  };

  const handleExport = () => {
    const csv = [headers.join(',')]
      .concat(rows.map((r) => [r.code, ...r.cells, r.category].join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'categorized.csv');
  };

  const filtered = rows
    .map((row, idx) => ({ row, idx }))
    .filter(({ row }) => {
      if (!filterValue) return true;
      return row.cells[filterColumn]
        ?.toLowerCase()
        .includes(filterValue.toLowerCase());
    });

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <OverlayWindow
        title="Batch Categorization"
        onClose={onClose}
        size={WindowSize.lg}
        body={
          <div className="space-y-4">
            <input
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileUpload}
            />
            {sheetNames.length > 0 && (
              <div className="flex items-center gap-2">
                <span>Sheet</span>
                <select
                  value={selectedSheet}
                  onChange={(e) => setSelectedSheet(Number(e.target.value))}
                  className="border p-1"
                >
                  {sheetNames.map((n, i) => (
                    <option key={n} value={i}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>Header Row</span>
                <input
                  type="number"
                  min={1}
                  value={headerRow + 1}
                  onChange={(e) => setHeaderRow(Number(e.target.value) - 1)}
                  className="border p-1 w-16"
                />
              </div>
            )}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {categories.map((c) => (
                  <span
                    key={c}
                    className="border px-2 py-1 rounded inline-flex items-center"
                  >
                    {c}
                    <button
                      type="button"
                      onClick={() => removeCategory(c)}
                      className="ml-1 text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="border p-1"
                />
                <Button theme="green-outline" onClick={addCategory}>
                  Add Category
                </Button>
              </div>
            </div>
            {headers.length > 0 && (
              <div className="flex items-center gap-2">
                <span>Filter by</span>
                <select
                  value={filterColumn}
                  onChange={(e) => setFilterColumn(Number(e.target.value))}
                  className="border p-1"
                >
                  {headers.slice(1, headers.length - 1).map((h, i) => (
                    <option key={`${i}-${h}`} value={i}>
                      {h}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="border p-1"
                />
              </div>
            )}
            {rows.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Button theme="green-outline" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button theme="green-outline" onClick={clearSelection}>
                    Clear Selection
                  </Button>
                  <Button theme="green-outline" onClick={autoCode}>
                    Auto Code
                  </Button>
                  <select
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                    className="border p-1"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <Button theme="green-outline" onClick={applyBulkCategory}>
                    Apply to Selected
                  </Button>
                </div>
                <div className="overflow-auto max-h-96">
                  <table className="min-w-full text-sm border">
                    <thead>
                      <tr>
                        <th className="border px-2 py-1"></th>
                        {headers.map((h, i) => (
                          <th key={`${i}-${h}`} className="border px-2 py-1">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(({ row, idx }) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="border px-2 py-1 text-center">
                            <input
                              type="checkbox"
                              checked={selected.has(idx)}
                              onChange={() => toggleSelect(idx)}
                            />
                          </td>
                          <td className="border px-2 py-1">{row.code}</td>
                          {row.cells.map((cell, i) => (
                            <td key={i} className="border px-2 py-1">
                              {cell}
                            </td>
                          ))}
                          <td className="border px-2 py-1">
                            <select
                              value={row.category}
                              onChange={(e) =>
                                updateCategory(idx, e.target.value)
                              }
                            >
                              {categories.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-right">
                  <Button theme="green-outline" onClick={handleExport}>
                    Export CSV
                  </Button>
                </div>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default BatchCategorizationModal;
